import type { ClearScope, ConnectionStatus, MessageSource } from '../messageSource'
import {
  isRecord,
  isSettled,
  PAYLOAD_MAX_BYTES,
  parseLatest,
  parseReady,
  parseRow,
  positiveInt,
  REFUSED_CLOSE_CODES,
  requestFrameRow,
  streamUrl,
  type Scope,
} from './contract'
import { createDisplayGate } from './displayGate'
import { createReceiverApi, isRefusal } from './receiverApi'

/** Reactivate tills send the display device requests over the API's shared
 *  WebSocket, the same pipeline that reaches printers and payment terminals.
 *  The display holds a read-only receiver grant from the till and nothing
 *  else. This file is the connection's life: connect, hello, ready, recover,
 *  receive, acknowledge, reconnect or stop. What a frame means lives in
 *  contract.ts, what the screen shows in displayGate.ts. */

/** No frame for this long, beats included, and the socket is presumed dead. */
const SILENCE_MS = 60_000
const BACKOFF_MAX_MS = 30_000
/** Acknowledgements in flight at once; more than this and new ones wait for
 *  the next row. */
const MAX_ACKS_IN_FLIGHT = 128

export interface DeviceRequestSourceOptions {
  apiUrl: string
  placeId: string
  userId: string
  /** null: the session was paired but has no grant now, so it waits. */
  token: string | null
  /** Hears what happened to the connection, for error reporting. Never given
   *  the grant. */
  report?: (event: ReceiverEvent) => void
}

/** Why a session stopped, or what went wrong on the way. */
export type RefusalReason = 'no_grant' | 'no_api' | 'ready_mismatch' | 'denied' | 'closed' | 'http'

export type ReceiverEvent =
  | { kind: 'refused'; reason: RefusalReason; code?: number | null }
  | { kind: 'reconnecting'; failures: number; code?: number }
  | { kind: 'ack_failed'; step: 'delivered' | 'outcome'; status: number | null }

function backoff(failures: number): number {
  return Math.min(BACKOFF_MAX_MS, 1000 * 2 ** Math.min(failures, 5)) * (0.75 + Math.random() * 0.5)
}

export function createDeviceRequestSource(options: DeviceRequestSourceOptions): MessageSource {
  const placeId = positiveInt(options.placeId)
  const userId = positiveInt(options.userId)

  return {
    brandingNeedsGrant: true,
    subscribe(listener) {
      let stopped = false
      let socket: WebSocket | null = null
      // Bumped on every new socket and on teardown, so callbacks of a socket
      // or a read that has been replaced fall on the floor.
      let epoch = 0
      let failures = 0
      let recoverFailures = 0
      let reconnectTimer: ReturnType<typeof setTimeout> | undefined
      let recoverTimer: ReturnType<typeof setTimeout> | undefined
      let silenceTimer: ReturnType<typeof setTimeout> | undefined
      let momentTimer: ReturnType<typeof setTimeout> | undefined

      const gate = createDisplayGate()
      // Live rows seen, so a slow latest-state read does not undo them.
      let observed = 0
      const acking = new Set<number>()

      const status = (next: ConnectionStatus) => {
        if (!stopped) listener.status?.(next)
      }
      const clear = (scope: ClearScope) => {
        clearTimeout(momentTimer)
        if (!stopped) listener.clear?.(scope)
      }

      const closeSocket = () => {
        epoch++
        clearTimeout(silenceTimer)
        clearTimeout(recoverTimer)
        const previous = socket
        socket = null
        if (!previous) return
        previous.onopen = previous.onmessage = previous.onerror = previous.onclose = null
        try {
          previous.close()
        } catch {
          // Ownership ends either way.
        }
      }

      /** The grant is gone. Nothing of this sale or this shop stays on
       *  screen, and the display waits for the till to pair it again. */
      const report = (event: ReceiverEvent) => options.report?.(event)

      const refuse = (reason: RefusalReason, code?: number | null) => {
        if (stopped) return
        report({ kind: 'refused', reason, code })
        clear('all')
        listener.branding?.(null)
        status('offline')
        stopped = true
        clearTimeout(reconnectTimer)
        closeSocket()
      }

      const reconnect = (code?: number) => {
        closeSocket()
        if (stopped) return
        status('offline')
        report({ kind: 'reconnecting', failures: failures + 1, code })
        clearTimeout(reconnectTimer)
        reconnectTimer = setTimeout(connect, backoff(failures++))
      }

      if (placeId === null || userId === null || options.token === null) {
        refuse('no_grant')
        return () => {}
      }
      const scope: Scope = { placeId, userId }
      const api = createReceiverApi(options.apiUrl, placeId, options.token)

      /** Shows a row if it is newer than what is on screen, then tells the API
       *  it arrived and was shown. The screen never waits on the
       *  acknowledgement. */
      const receive = async (raw: unknown, serverNowMs: number, stored: boolean) => {
        const row = parseRow(raw, scope, serverNowMs)
        if (!row) return
        const decision = gate.offer(row, serverNowMs)
        if (decision.kind === 'ignore') return
        if (!stored) observed++

        if (decision.kind === 'clear') clear(decision.scope)
        if (decision.kind === 'show') {
          clearTimeout(momentTimer)
          // `created` drives the 15 minute idle reset. Restate the server's
          // age on this display's clock.
          listener.message({ ...decision.message, created: Math.floor((Date.now() - decision.ageMs) / 1000) }, stored)
          if (decision.endsInMs !== undefined && decision.endScope) {
            const owner = row.id
            const endScope = decision.endScope
            momentTimer = setTimeout(() => {
              if (!stopped && gate.highestId === owner) listener.clear?.(endScope)
            }, decision.endsInMs)
          }
        }

        if (row.status === 'fulfilled' || isSettled(row.status) || row.payload.invalidated) return
        if (acking.has(row.id) || acking.size >= MAX_ACKS_IN_FLIGHT) return

        const mine = epoch
        acking.add(row.id)
        try {
          if (row.status === 'requested') {
            const delivered = await api.delivered(row.id)
            if (stopped || mine !== epoch) return
            if (!delivered.ok) {
              report({ kind: 'ack_failed', step: 'delivered', status: delivered.status })
              if (isRefusal(delivered)) refuse('http', delivered.status)
              else if (delivered.status === 409) void recover()
              // A timeout is unknown: not retried, never called fulfilled.
              return
            }
          }
          const outcome = await api.fulfilled(row.id)
          if (!outcome.ok) report({ kind: 'ack_failed', step: 'outcome', status: outcome.status })
          if (!stopped && mine === epoch && isRefusal(outcome)) refuse('http', outcome.status)
        } finally {
          acking.delete(row.id)
        }
      }

      /** One read of the current screen after every (re)connect. The socket is
       *  already subscribed, so nothing sent during the read is lost. */
      const recover = async () => {
        if (stopped) return
        const mine = epoch
        const before = observed
        const answer = await api.latest(scope.userId)
        if (stopped || mine !== epoch) return
        if (isRefusal(answer)) return refuse('http', answer.status)

        const latest = answer.ok ? parseLatest(answer.data) : null
        if (latest?.kind === 'current') {
          recoverFailures = 0
          void receive(latest.row, latest.serverNowMs, true)
          return
        }
        if (latest?.kind === 'empty') {
          recoverFailures = 0
          // A live update that came in during the read is newer than this.
          if (observed === before) clear('all')
          return
        }

        if (observed !== before) return
        // Unknown is not empty: the screen stays as it was until a read works.
        clearTimeout(recoverTimer)
        recoverTimer = setTimeout(() => void recover(), backoff(recoverFailures++))
      }

      const connect = () => {
        if (stopped) return
        const url = streamUrl(options.apiUrl)
        // A build with no API configured can never reach the till.
        if (!url) return refuse('no_api')
        if (typeof WebSocket !== 'function') return reconnect()

        const mine = ++epoch
        let ready = false
        let next: WebSocket
        try {
          next = new WebSocket(url)
        } catch {
          return reconnect()
        }
        socket = next
        const current = () => !stopped && socket === next && epoch === mine
        const armSilence = () => {
          clearTimeout(silenceTimer)
          silenceTimer = setTimeout(() => {
            if (current()) reconnect()
          }, SILENCE_MS)
        }
        armSilence()

        next.onopen = () => {
          if (!current()) return
          try {
            // The grant goes in this first frame only, never in the URL.
            next.send(JSON.stringify({ t: 'hello', v: 1, receiver_token: options.token }))
          } catch {
            reconnect()
          }
        }

        next.onmessage = (event: MessageEvent) => {
          if (!current()) return
          armSilence()
          if (typeof event.data !== 'string' || event.data.length > PAYLOAD_MAX_BYTES + 4096) return reconnect()
          let frame: unknown
          try {
            frame = JSON.parse(event.data)
          } catch {
            return reconnect()
          }
          if (!isRecord(frame)) return reconnect()

          switch (frame.t) {
            case 'denied':
              if (typeof frame.close_code === 'number' && REFUSED_CLOSE_CODES.includes(frame.close_code)) return refuse('denied', frame.close_code)
              // Otherwise the close that follows says what to do.
              return
            case 'ready': {
              // The grant must be for this store and this owner, or nothing of
              // it is shown, branding included. Branding is reread on every
              // connection, so a change shows on the next reconnect.
              const accepted = parseReady(frame, scope)
              if (!accepted) return refuse('ready_mismatch')
              ready = true
              failures = 0
              status('online')
              listener.branding?.(accepted.branding)
              void recover()
              return
            }
            case 'request': {
              if (!ready) return
              const announced = requestFrameRow(frame)
              if (announced) void receive(announced.raw, announced.serverNowMs, announced.stored)
              return
            }
            default:
              // beat, or a frame this display does not use.
              return
          }
        }

        next.onerror = () => {
          if (current()) reconnect()
        }
        next.onclose = (event: CloseEvent) => {
          if (!current()) return
          if (REFUSED_CLOSE_CODES.includes(event.code)) refuse('closed', event.code)
          else reconnect(event.code)
        }
      }

      connect()

      return () => {
        clearTimeout(reconnectTimer)
        clearTimeout(momentTimer)
        closeSocket()
        stopped = true
      }
    },
  }
}
